'use client';

import { useState, useEffect, useCallback } from 'react';
import { offlineStorage } from '@/lib/services/offline-storage';
import { useToast } from '@/hooks/use-toast';

interface UseOfflineSyncResult {
  isOnline: boolean;
  isSyncing: boolean;
  lastSyncTime: Date | null;
  pendingChanges: number;
  sync: () => Promise<void>;
}

export function useOfflineSync(): UseOfflineSyncResult {
  const [isOnline, setIsOnline] = useState<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [pendingChanges, setPendingChanges] = useState<number>(0);
  const { toast } = useToast();

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast({
        title: "Conexão restaurada",
        description: "Seus dados serão sincronizados automaticamente.",
        duration: 3000,
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast({
        title: "Sem conexão",
        description: "O app continuará funcionando offline.",
        duration: 3000,
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [toast]);

  // Check for pending changes periodically
  useEffect(() => {
    let mounted = true;

    const checkPendingChanges = async () => {
      try {
        const pendingMeals = await offlineStorage.getPendingMeals();
        if (mounted) {
          setPendingChanges(pendingMeals.length);
        }
      } catch (error) {
        console.error('Error checking pending changes:', error);
      }
    };

    checkPendingChanges();
    const interval = setInterval(checkPendingChanges, 30000); // Check every 30 seconds

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  // Sync function
  const sync = useCallback(async () => {
    if (!isOnline || isSyncing) return;

    try {
      setIsSyncing(true);
      const pendingMeals = await offlineStorage.getPendingMeals();

      if (pendingMeals.length === 0) {
        setLastSyncTime(new Date());
        return;
      }

      // Sync each pending meal
      for (const meal of pendingMeals) {
        try {
          // TODO: Replace with your actual API endpoint
          const response = await fetch('/api/meals', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(meal.data),
          });

          if (response.ok) {
            await offlineStorage.markAsSynced(meal.id);
          } else {
            throw new Error('Failed to sync meal');
          }
        } catch (error) {
          console.error('Error syncing meal:', error);
          toast({
            title: "Erro na sincronização",
            description: "Alguns dados não puderam ser sincronizados.",
            variant: "destructive",
          });
        }
      }

      // Clean up old synced data
      await offlineStorage.clearSyncedData();
      
      const remainingPending = await offlineStorage.getPendingMeals();
      setPendingChanges(remainingPending.length);
      setLastSyncTime(new Date());

    } catch (error) {
      console.error('Sync error:', error);
      toast({
        title: "Erro na sincronização",
        description: "Não foi possível sincronizar seus dados.",
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, isSyncing, toast]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline && pendingChanges > 0) {
      sync();
    }
  }, [isOnline, pendingChanges, sync]);

  return {
    isOnline,
    isSyncing,
    lastSyncTime,
    pendingChanges,
    sync,
  };
}